import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { stars } from "@/mocks/stars";
import Breadcrumb from "@/components/base/Breadcrumb";
import SectionHeading from "@/components/base/SectionHeading";

export default function Stars() {
  const [keyword, setKeyword] = useState("");
  const [sort, setSort] = useState("popular");

  const list = useMemo(() => {
    const filtered = stars.filter(
      (s) => s.name.includes(keyword.trim()) || s.enName.toLowerCase().includes(keyword.trim().toLowerCase()),
    );
    if (sort === "letter") filtered.sort((a, b) => a.letter.localeCompare(b.letter));
    else filtered.sort((a, b) => b.popularity - a.popularity);
    return filtered;
  }, [keyword, sort]);

  return (
    <div className="mx-auto w-full max-w-[1400px] px-4 py-8 md:px-6 md:py-10">
      <Breadcrumb items={[{ label: "明星资讯" }]} />

      <header className="mt-5 rounded-lg bg-primary-950 p-6 md:p-8">
        <span className="font-display text-xs tracking-[0.3em] text-primary-300">STARS</span>
        <h1 className="mt-2 text-2xl font-black text-background-50 md:text-3xl">明星资讯</h1>
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-background-300 md:text-base">
          汇集激情影视领域的实力演员与新锐面孔，提供个人小传与代表作品汇总，点击即可了解他们的更多动态。
        </p>
      </header>

      <div className="mt-6 flex flex-col gap-4 rounded-lg border border-background-200 bg-background-100/60 p-4 md:flex-row md:items-center md:justify-between">
        <div className="relative flex h-11 w-full items-center rounded-md border border-background-200 bg-background-50 focus-within:border-primary-400 md:max-w-xs">
          <i className="ri-search-line ml-3 text-foreground-500" />
          <input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="搜索明星名称"
            className="h-full w-full bg-transparent px-2 text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
          />
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setSort("popular")}
            className={`whitespace-nowrap rounded-md border px-4 py-2 text-sm font-medium transition-colors cursor-pointer ${
              sort === "popular"
                ? "border-primary-500 bg-primary-500 text-background-50"
                : "border-background-200 bg-background-50 text-foreground-700 hover:border-primary-300"
            }`}
          >
            按人气排序
          </button>
          <button
            type="button"
            onClick={() => setSort("letter")}
            className={`whitespace-nowrap rounded-md border px-4 py-2 text-sm font-medium transition-colors cursor-pointer ${
              sort === "letter"
                ? "border-primary-500 bg-primary-500 text-background-50"
                : "border-background-200 bg-background-50 text-foreground-700 hover:border-primary-300"
            }`}
          >
            按字母排序
          </button>
        </div>
      </div>

      <section className="mt-8">
        <SectionHeading eyebrow="Roster" title="演员名录" desc={`共收录 ${list.length} 位演员`} />
        {list.length > 0 ? (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {list.map((star) => (
              <article
                key={star.id}
                className="rounded-lg border border-background-200 bg-background-50 p-5 transition-colors hover:border-primary-300"
              >
                <div className="flex items-center gap-4">
                  <div className="h-20 w-20 shrink-0 overflow-hidden rounded-lg bg-background-200">
                    <img
                      src={star.avatar}
                      alt={`${star.name}头像`}
                      title={`${star.name} - 激情视频`}
                      className="h-full w-full object-cover object-top"
                    />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-lg font-bold text-foreground-950">{star.name}</h3>
                    <p className="font-display text-xs tracking-widest text-foreground-400">
                      {star.enName}
                    </p>
                    <span className="mt-2 inline-flex rounded-md bg-accent-100 px-2 py-0.5 text-xs font-medium text-accent-900">
                      {star.role}
                    </span>
                  </div>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-foreground-600 line-clamp-3">{star.bio}</p>
                <div className="mt-4 border-t border-background-200 pt-3">
                  <p className="text-xs font-medium text-foreground-500">代表作品</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {star.works.map((work) => (
                      <Link
                        key={work}
                        to={`/search?q=${encodeURIComponent(work)}`}
                        className="inline-flex rounded-md border border-background-200 bg-background-100 px-2.5 py-1 text-xs text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600 cursor-pointer"
                      >
                        {work}
                      </Link>
                    ))}
                  </div>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center text-sm text-foreground-500">
            未找到匹配的明星，换个名字试试吧。
          </div>
        )}
      </section>
    </div>
  );
}